import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import { selectSelectedCity } from "src/store/ui"
import {
  selectCityMetadataByUser,
  selectCurrentTeam,
  selectGame,
  selectGamePlayers,
  selectIsReviewing,
  selectMovements,
  selectPlayer,
  selectResearchLevels,
  selectViewerId,
} from "src/store/game"
import { selectGameConfig } from "src/store/common"
import { cellDisplayName, forceDisclosure, getPlace, movementsTouching } from "src/utils/maps"
import { teamsPresentAt } from "src/utils/siege"
import { UPGRADE_CURRENCY_ICON, upgradeGate } from "src/utils/buildingCosts"
import { upgradeOrdersFor } from "src/utils/cellActions"
import { productionCapacityFor } from "src/utils/productionCapacity"
import { cellFoodProduction } from "src/utils/foodOutlook"
import {
  populationGrowthMultiplier,
  researchOutput,
  researchPointMultiplier,
  siloRange,
  wallHealthMultiplier,
} from "src/utils/gameModifiers"

import constants from "src/constants"
import ModalService from "src/modals"
import { buildings } from "src/assets/images"

import Modal from "src/components/modals/Modal"
import UnitItem from "src/components/UnitItem"
import ProductionPanel from "src/components/ProductionPanel"
import CoordChip from "src/components/CoordChip"
import UnderConstruction from "src/components/UnderConstruction"
import RetiringNote from "src/components/RetiringNote"

import "./CityModal.css"
import { usePointerContext } from "src/utils/input"
import { scale } from "src/utils/ladder"

// Display names live in the `city.buildings.*` translation keys (camelCased backend code).
export const buildingNameKey = (code) => `city.buildings.${code.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`

/* The sheet over the map, ending above the action bar while one is up (`.modal-above-action-bar`,
   Modal.css; owner, 2026-09-30: "the modal should always leave space to the action modal").

   And drawn as the bar's upper half rather than as a popup over it (owner, 2026-10-02: "feel more
   integrated into the UI with the action modal"): the bar's panel, the bar's hairline, the bar's
   way of naming a cell, and a 6px seam between the two — the gap the bar leaves between its own
   blocks — where it used to hang from the map's top edge, 14px clear of the bar or a screen away
   from it, in the generic modal chrome. It stands on the bar now and grows upward from it, so a
   short sheet (open ground, an enemy city) sits against the bar instead of at the top of the map.
   No `top` here on purpose: the inline `alignItems` Modal writes for one would pin the sheet to
   the top again, and where it sits is `city-sheet`'s rule in CityModal.css. `width: 100%` is the
   phone's; off the phone the stylesheet caps it (owner, 2026-10-03: "maybe I can see a little bit
   of map if my screen is big enough"), so the map shows round a sheet that is short. */
const SHEET = {
  width: "100%",
  isolated: false,
  scope: "map",
  maxHeight: "100%",
  className: "modal-above-action-bar city-sheet",
}

const unitsIn = (metadata) =>
  Object.values(metadata?.units || {}).reduce((total, unit) => total + (unit.number || 0), 0)

/* The garrison as one row of unit chips (owner, 2026-10-03: "We don't need bars for the current
   army. Just increase the unit image, and put the number as we always do. Only 1 row"): the chip
   every list of units draws, the count in its corner, a little bigger than the roster's
   (`.city-unit-tiles`), wrapping when a city holds more kinds than the column is wide. A chip opens
   the unit's sheet, as it does everywhere. */
const ArmyTiles = ({ units, t }) => {
  const rows = Object.entries(units || {})
    .map(([type, data]) => ({ type, number: data?.number ?? 0 }))
    .filter((unit) => unit.number > 0)
  if (rows.length === 0) return <p className="city-note">{t("city.nobodyHere")}</p>
  return (
    <div className="city-unit-tiles" data-testid="city-army">
      {rows.map(({ type, number }) => (
        <UnitItem key={type} type={type} count={number} />
      ))}
    </div>
  )
}

/* What a cell is: who holds it, what it has built, what stands on it and what it is making. Its
   orders — march, colonize, settle, spy, strike, battle, the tunnel and disband — are the map's
   action bar's (`MapActionBar` over `utils/cellActions`; owner, 2026-09-30), which stays in sight
   below this sheet and whose keys keep working while the sheet is the only modal open — on the
   phone too (owner, 2026-10-03). What is pressed here is the city's own — a building's upgrade,
   the production queue. */
const CityModal = () => {
  const { t } = useTranslation()
  // The verb these hints use: a finger taps, a mouse clicks (see utils/input).
  const pointerContext = usePointerContext()
  const city = useSelector(selectSelectedCity)
  const cityMetadata = useSelector((state) => city && selectCityMetadataByUser(state, city))
  const userId = useSelector(selectViewerId)
  const team = useSelector(selectCurrentTeam)
  const players = useSelector(selectGamePlayers)
  const gameConfig = useSelector(selectGameConfig)
  const allMovements = useSelector(selectMovements)
  const researchLevels = useSelector(selectResearchLevels)
  const game = useSelector(selectGame)
  const ownerId = cityMetadata?.owner
  const owner = useSelector((state) => selectPlayer(state, ownerId))
  /* Looking, not playing: a replay, or a spectator watching through a seat. The panel shows every
     number it always showed — that is the whole point of being able to open a city in a replay —
     and offers no control that would reach a socket there is not one of. Folded into `isOwnCity`
     because every upgrade affordance already asks that question, so one term covers the building
     grid without thirteen more conditions. */
  const reviewing = useSelector(selectIsReviewing)

  if (!city) return null

  const { flag, country } = getPlace({ row: city.row, column: city.col })
  const battleBurningAt = (at) => (game?.metadata?.battles?.[`${at.col}-${at.row}`] ?? 0) > 0
  const name = cellDisplayName({ row: city.row, column: city.col, held: ownerId === userId })
  const ownerMetadata = cityMetadata?.metadata_by_user?.[ownerId]
  const ownMetadata = cityMetadata?.metadata_by_user?.[userId]
  const ownUnitsHere = unitsIn(ownMetadata)
  const isOwnCity = ownerId === userId && !reviewing
  const myTeam = team
  const playerOf = (id) => players.find((p) => p.user.id === Number(id)) || null

  // Stacks leaving, heading for, or passing through this cell — computed before the early
  // branches, because a march touching the cell is reason enough to open this view even when the
  // cell itself has nothing to show (2026-08-25).
  const teamOf = (id) => game?.metadata?.users?.[String(id)]?.team
  const touchingMovements = movementsTouching(allMovements, city, { userId, teamOf })
  // A stack that has reached this cell is listed with the army standing here, below; the
  // movements list is for what still goes somewhere — inbound, or standing here and going on
  // (owner, 2026-09-07). An arrived march has no "onwards" left to show.
  const listedMovements = touchingMovements.filter((movement) => !(movement.arrived && movement.standing))
  const movementUnitsText = (units) =>
    units.map((u) => `${u.number} ${t(`city.units.${u.type}`, { defaultValue: u.type })}`).join(" · ")
  const movementsSection =
    listedMovements.length > 0 ? (
      <div className="city-movements">
        <div className="city-section-head">
          <span className="section-label">{t("city.movementsLabel")}</span>
          <span className="city-rule" />
        </div>
        {listedMovements.map((movement) => (
          <p key={movement.id} className={`city-movement ${movement.relation}`}>
            <span className="city-movement-kind">
              ⚔{" "}
              {t(`game.tooltip.move.${movement.roles[0]}`, { who: t(`game.tooltip.move.${movement.relation}`) })}
            </span>{" "}
            <CoordChip col={movement.origin.col} row={movement.origin.row} /> →{" "}
            <CoordChip col={movement.destination.col} row={movement.destination.row} />
            {movement.halted
              ? ` · ${t("game.movements.heldUp")}`
              : movement.roundsLeft > 0
              ? ` · ${t("game.tooltip.move.eta", { count: movement.roundsLeft })}`
              : ""}
            {forceDisclosure(movement) === "units"
              ? ` · ${movementUnitsText(movement.units)}`
              : forceDisclosure(movement) === "unknown"
              ? ` · ${t("game.tooltip.move.unknown")}`
              : ""}
          </p>
        ))}
      </div>
    ) : null

  /* Everyone on this cell, garrisons *and* stacks standing on it.

     A combat that came out of a collision has both sides as movements and nothing at all in
     `metadata_by_user`, so a presence check that reads only garrisons sees an empty field: the
     sheet listed one army over a fight that was burning (owner, 2026-09-09). The engine's own
     clock answers that while a fight is unresolved, but it is cleared the moment one resolves — so
     two teams standing on one cell is the other half of it, and the two together are what this
     sheet, the map's fire and the action bar's BATTLE all read. */
  const stacksHere = touchingMovements.filter((movement) => movement.standing)
  const battleBurning = battleBurningAt(city)
  // `teamsPresentAt` is the one definition of who is on a cell, shared with the food projection
  // and the action bar — the whole reason `utils/siege` exists is that "in combat" must not come
  // to mean three different things on three screens, which is exactly what had happened here.
  const teamsHere = teamsPresentAt({
    coords: { column: Number(city.col), row: Number(city.row) },
    cells: { [`${city.col}-${city.row}`]: cityMetadata },
    users: game?.metadata?.users,
    movements: allMovements,
  })
  const battleHere = battleBurning || teamsHere.size > 1

  // A camp is a stack that has stopped here — arrived, or held by a fight — not one that stands
  // on its origin with its road ahead (owner, 2026-09-07: Akure listed "8 troops here" for an
  // order that had just left; those 8 are the movement, and the movements list already says so).
  // Where a battle is burning that distinction is moot: everything standing on the square is in
  // the fight and every player's units belong on the sheet.
  const standingStacks = stacksHere.filter(
    (movement) => movement.arrived || movement.halted || battleHere
  )
  // One row per *player*, not per movement (owner's S18): a force that arrived in three marches is
  // one force at the gates, and three rows for it read as three separate armies. Units are summed
  // per type (every stack comes with its units, 36/V1).
  const stacksByPlayer = {}
  standingStacks.forEach((movement) => {
    const key = String(movement.userId)
    const row = (stacksByPlayer[key] = stacksByPlayer[key] || {
      id: key,
      userId: movement.userId,
      relation: movement.relation,
      who: playerOf(movement.userId),
      halted: false,
      cameFrom: null,
      team: playerOf(movement.userId)?.team ?? null,
      units: {},
      total: 0,
    })
    row.halted = row.halted || Boolean(movement.halted)
    row.cameFrom = row.cameFrom || movement.cameFrom
    movement.units.forEach((unit) => {
      row.units[unit.type] = (row.units[unit.type] || 0) + unit.number
    })
    row.total += movement.units.reduce((sum, unit) => sum + unit.number, 0)
  })
  const stackRows = Object.values(stacksByPlayer).map((row) => ({
    ...row,
    units: Object.entries(row.units).map(([type, number]) => ({ type, number })),
  }))
  const standingSection =
    stackRows.length > 0 ? (
      <div className="city-stacks">
        {stackRows.map(({ id, relation, who, halted, cameFrom, team, units, total }) => (
          <div className={`city-intruder city-stack ${relation}`} key={id}>
            <b>{relation === "self" ? t("city.you") : who?.user.username ?? t("city.someone")}</b>{" "}
            {/* The wording follows the ground (owner, 2026-09-07): fighting here, at the gates of
                somebody else's city, or simply here on open land — never "on the march", which a
                stack standing still is not. */}
            {t(
              halted || battleBurningAt(city)
                ? "city.stackFightingHere"
                : owner && team && owner.team !== team
                ? "city.stackAtGates"
                : "city.stackHere",
              { count: total ?? 0 }
            )}
            {cameFrom && (
              <>
                {" · "}
                {t("city.stackCameFrom")} <CoordChip col={cameFrom.col} row={cameFrom.row} />
              </>
            )}
            {units.length > 0 && (
              <div className="city-intruder-units">
                {units.map((unit) => (
                  <UnitItem key={unit.type} type={unit.type} count={unit.number} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    ) : null

  const header = (
    <header className="city-head">
      {flag && <img src={flag} alt="" className="city-flag" />}
      {/* Named the way the bar names it — the town, its coordinate in mono beside it, and who
          holds it on the line under — so the sheet and the bar below read as one panel about one
          cell rather than two that happen to agree. */}
      <div className="city-head-title">
        <div className="city-head-name">
          <h1>{name}</h1>
          <span className="city-head-coords">
            {city.col}-{city.row}
          </span>
        </div>
        {/* The town names the cell; the country is what the flag beside it means. They are the same
            string only where a cell has no town of its own — Antarctica, mostly — and printing it
            twice there would read as a mistake. */}
        <p className="city-head-meta">
          {country && country !== name ? `${country} · ` : ""}
          {owner ? (
            <>
              {t("city.heldBy")} <b>{owner.user.username}</b>
            </>
          ) : (
            t("city.unclaimed")
          )}
        </p>
      </div>
    </header>
  )

  // Ground with no cell record at all: reachable only because a march touches it — that is the
  // info this view has to offer, so show it rather than nothing. That includes the stacks that
  // have stopped here: a march that arrived is listed with the army standing on the cell, not
  // with the movements, so a branch that drew only the movements dropped it entirely — on 111-39
  // in game 111, round 14, handx13's 145 arrived into a fight and the sheet showed only the
  // horde's 21 (a fight in the open has no cell record until ownership writes one).
  if (!cityMetadata) {
    if (!movementsSection && !standingSection) return null
    return (
      <Modal {...SHEET}>
        {header}
        <div className="city-body">
          {standingSection}
          {movementsSection}
        </div>
      </Modal>
    )
  }

  // Neutral ground: everyone standing on it, grouped by player (owner's R10 — a teammate's camp
  // used to show nothing, and your own only your own). Settle, where your troops can pay for it,
  // is on the map's action bar with every other order.
  // Ground nobody holds is neutral even with nobody on it. It used to need somebody standing
  // there, so a cell whose only stack had an order of this round (a march leaving, not a camp)
  // fell through to the fogged-enemy-city sheet: "sin reclamar" over "Territorio enemigo: aquí
  // no tienes ojos", while the movements below it were itemised (game 137, 42-20, round 21).
  const presentOnNeutral = Object.entries(cityMetadata.metadata_by_user || {})
    .map(([presentId, presentMetadata]) => ({
      id: Number(presentId),
      player: playerOf(presentId),
      units: Object.entries(presentMetadata?.units || {})
        .map(([type, data]) => ({ type, number: data?.number ?? 0 }))
        .filter((u) => u.number > 0),
    }))
    .filter(({ units }) => units.length > 0)
  if (ownerId == null) {
    return (
      <Modal {...SHEET}>
        {header}
        <div className="city-body">
          <p className="city-note">{ownUnitsHere > 0 ? t("city.unclaimedGround") : t("city.neutralGround")}</p>
          {presentOnNeutral.map(({ id, player, units }) => (
            <div
              className={`city-intruder${id === userId ? " own" : player?.team === myTeam ? " allied" : ""}`}
              key={id}
            >
              <b>{id === userId ? t("city.you") : player?.user.username ?? t("city.someone")}</b>{" "}
              {id === userId
                ? t("city.yourTroopsHere", { count: units.reduce((sum, u) => sum + u.number, 0) })
                : player?.team === myTeam
                  ? t("city.alliedGarrison", { count: units.reduce((sum, u) => sum + u.number, 0) })
                  : player?.team === "zombie"
                    ? t("city.standingHereZombies", { count: units.reduce((sum, u) => sum + u.number, 0) })
                    : t("city.standingHereTroops", { count: units.reduce((sum, u) => sum + u.number, 0) })}
              <div className="city-intruder-units">
                {units.map((unit) => (
                  <UnitItem key={unit.type} type={unit.type} count={unit.number} showLabel />
                ))}
              </div>
            </div>
          ))}
          {standingSection}
          {movementsSection}
        </div>
      </Modal>
    )
  }

  // Under fog of war an enemy city arrives with its coordinates and owner but no contents. Show
  // that honestly rather than crashing on the missing entry. Only a city reaches here: ground
  // nobody holds took the neutral branch above.
  if (!ownerMetadata) {
    return (
      <Modal {...SHEET}>
        {header}
        <div className="city-body">
          <p className="city-note">{t("city.enemyTerritory")}</p>
          {standingSection}
          {movementsSection}
        </div>
      </Modal>
    )
  }

  // Everything below describes the *owner's* city, so every lookup keys off the owner's team and
  // research — not the viewer's. Keyed off the viewer, a human inspecting a zombie city read the
  // putrefactor out of human_config and told the player it produced +0 a round.
  const configKey = (owner?.team ?? team) === "human" ? "human_config" : "zombie_config"
  const ownerResearchLevels = isOwnCity
    ? researchLevels
    : game?.metadata?.users?.[String(ownerId)]?.researches || {}
  // Whether we may quote a research-scaled number for this city (owner, 2026-09-09: "I'm not
  // supposed to know the enemy investigations"). The server no longer puts an enemy's `researches`
  // on the wire, so the test is simply whether it sent them — which is true of our own city, an
  // ally's, a spectator's view, a replay and a no-fog server, and false of an enemy under fog.
  // Without it every figure below would be computed at base and read as a fact about a city it
  // does not describe: rule 1, a number on screen is a claim about what the server will do.
  const ratesKnown = isOwnCity || "researches" in (game?.metadata?.users?.[String(ownerId)] || {})
  const teamBuildings = Object.values(constants.buildings[owner?.team || team] || {})
  const mainBuildingCode =
    owner?.team === "human" ? constants.buildings.human.COMMAND_CENTER : constants.buildings.zombie.HATCHERY
  const wallCode = constants.buildings.human.WALL
  const foodBuildingCode =
    owner?.team === "human"
      ? constants.buildings.human.CULTIVATION_CENTER
      : constants.buildings.zombie.PUTREFACTOR
  const researchBuildingCode =
    owner?.team === "human" ? constants.buildings.human.ACADEMY : constants.buildings.zombie.EVOLUTION_CHAMBER
  const siloCode = constants.buildings.human.MISSILE_SILO

  const levelOf = (code) => ownerMetadata.buildings?.[code]?.level || 0
  const perLevel = (code, key) => gameConfig?.[configKey]?.BUILDINGS?.[code]?.[key] || 0

  const wallMod = wallHealthMultiplier({ gameConfig, team: owner?.team, researchLevels: ownerResearchLevels })

  const workersHere = ownerMetadata.units?.[constants.units.human.WORKER]?.number || 0
  const wallAbsorbs = Math.trunc(
    (gameConfig?.rules_config?.WALL_HEALTH_PER_LEVEL ?? 0) * scale(levelOf(wallCode)) * wallMod
  )
  // `apply_research` multiplies what the building earns by the owner's race bonus, and this did
  // not — so a Technologist or an Intelligent, both of whom advertise "+25% Investigation Points",
  // were shown the un-bonused figure for their own city. Rule 1, the same shape as the five the
  // modifiers file was written for.
  const researchPerRound = Math.trunc(
    researchOutput({ rules: gameConfig?.rules_config, level: levelOf(researchBuildingCode) }) *
      researchPointMultiplier({ gameConfig, race: owner?.race })
  )

  // The four-number strip that stood here (army, troops, food, research) is gone (owner,
  // 2026-10-03: "Not useful"): the top bar carries the bank and its outlook, and every building's
  // card below says what it makes.
  // What each building is worth this round, in the unit the player thinks in.
  const buildingSummary = (code, level) => {
    if (level === 0) return { text: t("city.notBuiltYet"), tone: "warn" }
    // A foreign city says what it has, not what it makes: every summary below a human command
    // centre's is research-scaled, and the level is the part the map already shows.
    if (!ratesKnown && !(code === mainBuildingCode && owner?.team === "human")) {
      return { text: t("city.levelN", { level }) }
    }
    if (code === mainBuildingCode) {
      // For humans the command centre's headline is the *workers* it grows a round (owner's ask,
      // 2026-09-02) — the unit-build capacity is already the production queue's number, and
      // showing it here read as a second, contradictory worker figure. The horde has no workers,
      // so its hatchery keeps the unit-capacity summary.
      if (owner?.team === "human") {
        const growth =
          Math.trunc(perLevel(code, "WORKERS_PER_LEVEL") * populationGrowthMultiplier({
            gameConfig,
            team: owner?.team,
            race: owner?.race,
            researchLevels: ownerResearchLevels,
          })) *
          scale(level)
        // The worker's own face rather than "trab./rd" (owner's R32) — as the 👷 in the string
        // and not the unit sprite (owner, 2026-09-10): drawn at the card's caption size the
        // pixel-art worker is an unreadable smudge, and every other card here says its rate in
        // the same glyph the panel and the ledger use.
        return { text: t("city.workersPerRound", { amount: growth }) }
      }
      const capacity = productionCapacityFor({
        gameConfig,
        team: owner?.team,
        buildingLevel: level,
        researchLevels: ownerResearchLevels,
      })
      // Meat, not bodies — the hatchery's budget is what the queue may *cost* in a round, which is
      // how `build_from_hatchery` spends it and what its own building modal already says.
      return { text: t("city.buildsPerRound", { amount: capacity }) }
    }
    if (code === wallCode) return { text: t("city.absorbs", { amount: wallAbsorbs }) }
    if (code === foodBuildingCode) {
      // The number carries its unit (owner's Q3): a bare "+300 / rd" left the player guessing
      // whether the farm made food or grew workers.
      return {
        text: t(owner?.team === "zombie" ? "city.meatPerRound" : "city.foodPerRound", {
          amount: `+${cellFoodProduction({
            gameConfig,
            team: owner?.team,
            race: owner?.race,
            researchLevels: ownerResearchLevels,
            level,
            workers: workersHere,
          })}`,
        }),
      }
    }
    if (code === researchBuildingCode) return { text: t("city.pointsPerRound", { amount: `+${researchPerRound}` }) }
    if (code === siloCode) {
      const cooldownUntil = ownerMetadata.buildings?.[code]?.cooldown_until_tick ?? 0
      const roundsLeft = cooldownUntil - (game?.tick ?? 0)
      // A loaded silo holds one missile; its reach is the base plus the missile program's levels past the first (S13, U6), the server's
      // rule — the level buys damage only.
      const reach = siloRange({ gameConfig, researchLevels: ownerResearchLevels, level })
      return roundsLeft > 0
        ? { text: t("city.readyInRounds", { count: roundsLeft }), chip: t("city.reloading") }
        : { text: t("city.missilesReady", { count: 1, reach }) }
    }
    return { text: t("city.levelN", { level }) }
  }

  const queuedHere = Object.entries(ownerMetadata.buildings?.[mainBuildingCode]?.queue || {})
    .map(([type, data]) => ({ type, number: Number(data?.number ?? 0) }))
    .filter((unit) => unit.number > 0)

  const builtCodes = teamBuildings.filter((code) => ownerMetadata.buildings?.[code] !== undefined)
  const emptySlots = teamBuildings.length - builtCodes.length

  const openBuilding = (code, level) => ModalService.show("BuildingModal", { buildingCode: code, level })

  // "Not the owner" is not the same as "hostile" (2026-08-21): a teammate garrisoned in your city
  // is normal in a 2v2, and used to be painted as an intruder — and to fake a battle.
  const ownerTeam = players.find((p) => p.user.id === ownerId)?.team
  const otherPresences = Object.entries(cityMetadata.metadata_by_user)
    .filter(([presentId]) => Number(presentId) !== ownerId)
    .map(([presentId, presentMetadata]) => ({
      player: players.find((p) => p.user.id === Number(presentId)) || null,
      hostile: (players.find((p) => p.user.id === Number(presentId))?.team ?? null) !== ownerTeam,
      units: unitsIn(presentMetadata),
      // Fog already decided we may see this cell in full, so the breakdown is honest data, not a
      // leak: on your own city you watch exactly what you are fighting.
      unitsList: Object.entries(presentMetadata.units || {})
        .map(([type, data]) => ({ type, number: data?.number ?? 0 }))
        .filter((u) => u.number > 0),
    }))
    .filter(({ units }) => units > 0)

  // Everybody else on the cell, one row each (owner's sketch, 2026-10-03: "Other player troops"):
  // the garrisons of other players first, then the stacks that have stopped here.
  const othersHere = otherPresences.length > 0 || stackRows.length > 0

  return (
    <Modal {...SHEET}>
      {header}

      {/* Two columns (owner's sketch, 2026-10-03): what the city has built and what is coming to
          it on the left; what stands in it, what it is making and who else is here on the right.
          One column once the sheet is narrow (`@container modal`, CityModal.css). */}
      <div className="city-body city-grid">
        <div className="city-col" data-testid="city-col-left">
          <div className="city-section-head">
            <span className="section-label">{t("city.buildingsLabel")}</span>
            <span className="city-rule" />
            <span className="city-section-hint">{t(reviewing ? "city.buildingsHintReview" : "city.buildingsHint", { context: pointerContext })}</span>
          </div>

          <div className="building-row">
            {builtCodes.map((code) => {
              const level = levelOf(code)
              const summary = buildingSummary(code, level)
              // One level a building a round, landing when it turns (owner, 2026-10-02).
              const orders = upgradeOrdersFor({
                game,
                gameConfig,
                userId,
                team: owner?.team,
                race: owner?.race,
                col: city.col,
                row: city.row,
                buildingCode: code,
              })
              const gate = upgradeGate({
                gameConfig,
                team: owner?.team,
                buildingCode: code,
                level,
                race: owner?.race,
                workersOnCell: ownerMetadata.units?.[constants.units.human.WORKER]?.number ?? 0,
                food: game?.metadata?.users?.[String(userId)]?.food ?? 0,
                ...orders,
              })
              const gateReason = gate.reason ? t(`city.upgradeBlocked.${gate.reason}`) : null
              // The price arrow upgrades directly when the server would say yes (owner's P8) and is
              // greyed with the reason when it would say no (P2) — no modal round-trip either way.
              const quickUpgrade = (event) => {
                event.stopPropagation()
                if (!isOwnCity || !gate.allowed) return
                wsClient.send("game", { action: "level_up_building", column: city.col, row: city.row, building: code })
              }
              const cancelUpgrade = (event) => {
                event.stopPropagation()
                if (!isOwnCity) return
                wsClient.send("game", { action: "cancel_upgrade", column: city.col, row: city.row, building: code })
              }
              return (
                /* An unbuilt building is said by the picture going grey, not by a chip (owner,
                   2026-09-10): the words and the price arrow keep their colour, so what is left to
                   read is the name, what it would do, and what it costs. */
                <button
                  className={`building-card${level === 0 ? " not-built" : ""}${orders.ordered ? " upgrading" : ""}`}
                  key={code}
                  data-building={code}
                  onClick={() => openBuilding(code, level)}
                >
                  <img src={buildings[code]} alt="" className="building-art" />
                  {/* A level on its way (owner, 2026-10-03): a dark band across the middle of the
                      picture with the pick at work and the word, an orange frame, and no price in
                      the corner. Pressing the band takes the order back, there and then. */}
                  {orders.ordered && (
                    <span
                      role="button"
                      tabIndex={isOwnCity ? 0 : -1}
                      className="building-upgrading"
                      title={t("city.cancelUpgradeHint", { level: level + 1 })}
                      onClick={cancelUpgrade}
                      onKeyDown={(event) => event.key === "Enter" && cancelUpgrade(event)}
                    >
                      <UnderConstruction level={level + 1} className="building-construction" decorative />
                      <span className="building-upgrading-text">{t("city.upgrading")}</span>
                    </span>
                  )}
                  {/* Outside `.building-chips` on purpose: the chips row is inset 10px and is what
                      an absolute child would measure from, and this one is notched into the card's
                      own corner. */}
                  {level > 0 && <span className="building-level">{t("city.lvl", { level })}</span>}
                  <span className="building-chips">
                    {/* The reloading chip used to *replace* the price arrow, so a silo could not be
                        upgraded while it reloaded — a rule the server never had (S23). Both now. */}
                    {summary.chip ? <span className="building-flag">{summary.chip}</span> : null}
                    {orders.ordered ? null : (
                      <span
                        role="button"
                        tabIndex={isOwnCity && gate.allowed ? 0 : -1}
                        className={`building-cost${isOwnCity && gate.allowed ? " can-upgrade" : ""}${
                          isOwnCity && !gate.allowed ? " blocked" : ""
                        }`}
                        title={gateReason || (isOwnCity ? t("city.building.upgrade") : undefined)}
                        onClick={quickUpgrade}
                        onKeyDown={(event) => event.key === "Enter" && quickUpgrade(event)}
                      >
                        ⬆ {gate.cost ?? "?"} {UPGRADE_CURRENCY_ICON[owner?.team] ?? ""}
                      </span>
                    )}
                  </span>
                  <span className="building-foot">
                    <span className="building-name">{t(buildingNameKey(code), { defaultValue: code })}</span>
                    <span className={`building-summary${summary.tone ? ` ${summary.tone}` : ""}`}>
                      {summary.text}
                    </span>
                  </span>
                </button>
              )
            })}
            {emptySlots > 0 && (
              <div className="building-card empty">
                <span className="building-empty-plus">+</span>
                <span className="building-empty-label">
                  {t("city.slots", { count: emptySlots })}
                </span>
              </div>
            )}
          </div>

          {movementsSection}
        </div>

        <div className="city-col" data-testid="city-col-right">
          <section className="city-troops">
            <div className="city-section-head">
              <span className="section-label">{t("city.garrison")}</span>
              <span className="city-rule" />
            </div>
            <ArmyTiles units={ownerMetadata.units} t={t} />
            {/* The viewer's discharge ordered here, if any (owner, 2026-10-23): what retires when
                the round turns, with the ✕ that takes it back. Renders nothing otherwise. */}
            {!reviewing && <RetiringNote city={city} />}
            {/* What is on order, right under the army it will join (owner, 2026-10-03: "I liked the
                unit queue where it was before, under the ARMY unit list"), as the same chips. The
                levers that set it are the production panel below. */}
            {isOwnCity && queuedHere.length > 0 && (
              <>
                <div className="city-section-head">
                  <span className="section-label">{t("city.inQueue")}</span>
                  <span className="city-rule" />
                </div>
                <div className="city-unit-tiles" data-testid="city-queue">
                  {queuedHere.map(({ type, number }) => (
                    <UnitItem key={type} type={type} count={number} />
                  ))}
                </div>
              </>
            )}
          </section>

          {isOwnCity && (
            <section className="city-queue">
              <ProductionPanel city={city} />
            </section>
          )}

          {/* Fog of war decides whether we have this at all: on your own and allied cells you see
              everything, so anyone standing here shows up — which is how you learn an attack has
              arrived. Elsewhere metadata_by_user is blanked and the list is simply empty. */}
          {othersHere && (
            <section className="city-others">
              <div className="city-section-head">
                <span className="section-label">{t("city.othersHere")}</span>
                <span className="city-rule" />
              </div>
              {otherPresences.map(({ player, units, unitsList, hostile }) => (
                <div
                  className={`city-intruder city-presence${hostile ? "" : " allied"}`}
                  key={player?.user.id ?? "unknown"}
                  data-testid={`city-presence-${player?.user.id ?? "unknown"}`}
                >
                  <b>{player ? player.user.username : t("city.someone")}</b>{" "}
                  {hostile
                    ? player?.team === "zombie"
                      ? t("city.standingHereZombies", { count: units })
                      : t("city.standingHereTroops", { count: units })
                    : t("city.alliedGarrison", { count: units })}
                  {unitsList.length > 0 && (
                    <div className="city-intruder-units">
                      {unitsList.map((unit) => (
                        <UnitItem key={unit.type} type={unit.type} count={unit.number} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {standingSection}
            </section>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default CityModal
